import {
  CopyToClipboard,
  GhostIconButton,
  Icon,
  Text,
} from "@certn/cascadia-react";
import { CascadiaStandardIcon } from "@certn/cascadia-tokens";
import { css, cx } from "@emotion/css";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  whatsAppHref: string;
  smsHref: string;
  mailtoHref: string;
  onCopyLink: () => void;
  onShareMore: () => void;
  linkCopied: boolean;
  copyLinkTestID?: string;
  shareMoreTestID?: string;
}

export function ReferralCodeHero({
  code,
  testID,
}: {
  code: string;
  testID: string;
}) {
  return (
    <div className={styles.codeBox} data-testid={testID}>
      <div className={styles.codeText}>
        <Text as="span" style="global/heading-large" align="center">
          {code}
        </Text>
      </div>
      <div className={styles.copySlot}>
        <CopyToClipboard
          text={code}
          testID={`${testID}-copy`}
          hideText
          isHiddenUntilHover={false}
        />
      </div>
    </div>
  );
}

export function ReferralShareButtons({
  whatsAppHref,
  smsHref,
  mailtoHref,
  onCopyLink,
  onShareMore,
  linkCopied,
  copyLinkTestID = "copy-referral-share-url",
  shareMoreTestID = "share-more",
}: Props) {
  const { t } = useTranslation();
  const copyLinkLabel = linkCopied
    ? t("✓ Link copied")
    : t("Copy referral link");

  return (
    <div className={styles.shareRow} data-testid="referral-share-icons">
      <GhostIconButton
        icon={linkCopied ? "check" : "link"}
        size="medium"
        color="icon-accent-green"
        hoverColor="icon-accent-green-strong"
        className={styles.iconControl}
        aria-label={copyLinkLabel}
        onPress={onCopyLink}
        testID={copyLinkTestID}
      />
      <ShareIconLink
        href={mailtoHref}
        icon="mail"
        label={t("Email")}
        testID="share-email"
      />
      <ShareIconLink
        href={whatsAppHref}
        label={t("WhatsApp")}
        testID="share-whatsapp"
      >
        <WhatsAppGlyph />
      </ShareIconLink>
      <ShareIconLink
        href={smsHref}
        icon="chat_bubble"
        label={t("SMS")}
        testID="share-sms"
      />
      <GhostIconButton
        icon="send"
        size="medium"
        color="icon-accent-green"
        hoverColor="icon-accent-green-strong"
        className={styles.iconControl}
        aria-label={t("More")}
        onPress={onShareMore}
        testID={shareMoreTestID}
      />
    </div>
  );
}

function ShareIconLink({
  href,
  icon,
  label,
  testID,
  children,
}: {
  href: string;
  icon?: CascadiaStandardIcon;
  label: string;
  testID: string;
  children?: ReactNode;
}) {
  return (
    <a
      href={href}
      aria-label={label}
      title={label}
      data-testid={testID}
      className={cx(styles.iconControl, styles.iconLink)}
    >
      {children ?? (
        <Icon name={icon ?? "mail"} size="space-6" color="icon-accent-green" />
      )}
    </a>
  );
}

function WhatsAppGlyph() {
  return (
    <i className={styles.whatsAppIcon} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" focusable="false">
        <path
          fill="currentColor"
          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"
        />
      </svg>
    </i>
  );
}

const styles = {
  shareRow: css({
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--space-1)",
  }),
  iconControl: css({
    appearance: "none",
    WebkitAppearance: "none",
    WebkitTapHighlightColor: "transparent",
    background: "transparent",
    padding: 0,
    "&:hover, &:hover:enabled, &[data-hovered], &:active, &[data-pressed]": {
      background: "var(--background-ghost-hover)",
      color: "var(--icon-accent-green-strong)",
    },
    "&:hover > i, &:hover:enabled > i, &[data-hovered] > i, &:active > i, &[data-pressed] > i":
      {
        color: "var(--icon-accent-green-strong)",
      },
    "&:focus": {
      outline: "none",
    },
    "&:focus-visible, &[data-focus-visible]": {
      outline: "none",
      boxShadow: "var(--focus-action)",
    },
  }),
  iconLink: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "var(--space-10)",
    height: "var(--space-10)",
    border: "none",
    borderRadius: "var(--border-radius-2)",
    color: "var(--icon-accent-green)",
    textDecoration: "none",
    cursor: "pointer",
  }),
  whatsAppIcon: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    width: "1em",
    height: "1em",
    fontSize: "var(--space-6)",
    lineHeight: 1,
    fontStyle: "normal",
    "> svg": {
      display: "block",
      width: "1em",
      height: "1em",
      transform: "scale(0.83)",
      transformOrigin: "center",
    },
  }),
  codeBox: css({
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--space-2)",
    width: "max-content",
    maxWidth: "100%",
    padding: "var(--space-3)",
    border: "none",
    backgroundColor: "var(--background-brand-primary-weak)",
    borderRadius: "var(--border-radius-2)",
  }),
  codeText: css({
    display: "flex",
    justifyContent: "center",
    minWidth: 0,
  }),
  copySlot: css({
    display: "flex",
    flexShrink: 0,
  }),
};
